import { useState, type ReactNode } from 'react';
import { Close } from '@react-apps/design-system';
import { IconButton } from '@/shared/components/icon-button';

/**
 * Uma linha de dica que sai de vez com um clique.
 *
 * Existe porque há duas coisas nesta tela que ninguém descobre sozinho — que o
 * número do gabarito é clicável, e que a imagem no enunciado se move
 * arrastando — e as duas pediam a mesma peça. Sem isto, seria a segunda cópia
 * de "linha com 💡, botão de fechar e `localStorage`".
 *
 * O formato é deliberadamente pequeno: uma linha, **no lugar onde a coisa
 * acontece**, dispensável e permanentemente dispensada. Sem coach mark, sem
 * overlay, sem passo a passo e sem biblioteca de onboarding — a dica ensina uma
 * ação, não conduz um tour.
 */
export function DismissibleHint({
  storageKey,
  children,
}: {
  /** Chave da dispensa. Prefixada por tela para duas dicas não se confundirem. */
  storageKey: string;
  children: ReactNode;
}) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      // Storage bloqueado (aba anônima, política do browser): a dica aparece de
      // novo, e é só isso. Não poder lembrar não é motivo para quebrar a tela.
      return false;
    }
  });

  if (dismissed) return null;

  return (
    <div className="border-(--color-accent)/30 bg-(--color-accent)/5 flex items-start gap-2 rounded-lg border px-3 py-2">
      <p className="text-(--color-text-primary) flex-1 text-xs">
        💡 {children}
      </p>
      <IconButton
        label="Dispensar a dica"
        icon={<Close size="xs" />}
        onClick={() => {
          setDismissed(true);
          try {
            localStorage.setItem(storageKey, '1');
          } catch {
            // Não poder lembrar a dispensa não é motivo para não dispensar.
          }
        }}
      />
    </div>
  );
}
